import { useTranslation } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { useEffect, useState } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import GoogleMap from "@Components/Map";
import IconComponent from "@CubeComponents/icons";

/**
 * The project's location, as this modal reads and writes it. NOTE A SCHEMA GAP:
 * `projectDataItemSchema` declares none of these fields, even though `saveProjectsData` persists them
 * — so the shape lives here rather than in Zod. `address` is the wire form (an array of up to four
 * lines); `address1`–`address4` are the per-input working copies this modal splits it into.
 */
type ProjectLocation = {
  /** Array of address lines on the wire; the map pin overwrites it with one formatted string. */
  address?: string[] | string | null;
  address1?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  coordinates?: { lat?: number; lng?: number } | null;
  mapZoomLevel?: number;
  pincode?: string;
  city?: string;
  state?: string;
  country?: string;
  street?: string;
};

interface EditLocationModalProps {
  onAddClick: (data: ProjectLocation) => void;
  /** Absent on the dashboard surface, which has no separate cancel affordance. */
  onCancel?: (() => void) | undefined;
  isEditable?: boolean;
  locationData?: ProjectLocation;
  /** Opens the modal straight away instead of behind the edit pencil. */
  openDirectModal?: boolean;
  /** Flipped by the parent to force the read-only map to re-render. */
  refreshMap?: boolean;
}

function EditLocationModal({
  onAddClick,
  onCancel,
  isEditable = false,
  locationData = {},
  openDirectModal = false,
  refreshMap = false,
}: EditLocationModalProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<ProjectLocation>({});
  const [openLocationModal, setOpenLocationModal] = useState(openDirectModal);
  const [textBoxVisibleState, setTextBoxVisibleState] = useState(false);

  useEffect(() => {
    const tempLocationData = { ...locationData };
    const address = locationData.address;
    if (address) {
      tempLocationData.address1 = address[0];
      tempLocationData.address2 = address[1];
      tempLocationData.address3 = address[2];
    }
    setData(tempLocationData);
  }, [locationData, refreshMap]);

  const handleLocationDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleModalOpen = () => {
    setOpenLocationModal(true);
  };

  const handleModalClose = () => {
    setOpenLocationModal(false);
    if (onCancel) {
      onCancel();
    }
  };

  const textBoxVisible = () => {
    setTextBoxVisibleState(!textBoxVisibleState);
  };

  const onSuccess = () => {
    let saveAddressData: string[] | null = null;
    if (Object.keys(data).length) {
      const address: string[] = [];
      if (data.address1) {
        address.push(data.address1);
      }
      if (data.address2) {
        address.push(data.address2);
      }
      if (data.address3) {
        address.push(data.address3);
      }
      if (data.address4) {
        address.push(data.address4);
      }
      saveAddressData = address;
    }

    onAddClick({
      ...data,
      address: saveAddressData,
    });
    handleModalClose();
  };

  /**
   * `GoogleMap` calls this with `{ current, selected, selectedPinAddress, mapZoomLevel }`.
   * `selectedPinAddress` is the reverse-geocoded address it builds from the Places result.
   */
  const getLocationCallBack = ({
    selected = null,
    selectedPinAddress,
    mapZoomLevel,
  }: {
    current?: ProjectLocation["coordinates"];
    selected?: ProjectLocation["coordinates"];
    selectedPinAddress: {
      fullAddress?: string;
      street?: string;
      city?: string;
      state?: string;
      country?: string;
      postalCode?: string;
    };
    mapZoomLevel?: number;
  }) => {
    const pinData: ProjectLocation = {
      coordinates: selected,
      mapZoomLevel,
    };
    if (selectedPinAddress.postalCode) {
      pinData.pincode = selectedPinAddress.postalCode;
    }
    if (selectedPinAddress.fullAddress) {
      // KNOWN INCONSISTENCY, preserved verbatim: `address` is an array of lines everywhere else (see
      // `onSuccess`), but the map pin writes the single formatted string into it. Declared as a union
      // on `ProjectLocation.address` so the write needs no assertion at all.
      pinData.address = selectedPinAddress.fullAddress;
      pinData.address1 = selectedPinAddress.fullAddress;
    }
    if (selectedPinAddress.state) {
      pinData.state = selectedPinAddress.state;
    }
    if (selectedPinAddress.country) {
      pinData.country = selectedPinAddress.country;
    }
    if (selectedPinAddress.city) {
      pinData.city = selectedPinAddress.city;
    }
    if (selectedPinAddress.street) {
      pinData.street = selectedPinAddress.street;
    }
    setData((data) => ({ ...data, ...pinData }));
  };

  return (
    <>
      <div className="group relative flex h-full w-full">
        {locationData.coordinates ? (
          !openLocationModal && (
            <GoogleMap
              allowPanning={false}
              allowManualSelection={false}
              allowFullScreen={false}
              allowSearch={false}
              showLiveLocation={false}
              autoFetchLiveLocation={false}
              allowRecenterBtns={false}
              // getLocationCallBack={getLocationCallBack}
              // @ts-expect-error -- the stored location has `lat`/`lng` optional, but Google Maps' `LatLng` requires both. REAL GAP: a location saved without coordinates reaches the map.
              additionalMarker={locationData.coordinates}
              zoomLevel={locationData.mapZoomLevel}
            />
          )
        ) : (
          <div className="flex flex-col w-full gap-2 justify-center items-center">
            <IconComponent name="locationDenied" width="6rem" height="6rem" />
            {t("No Location set for this project")}
          </div>
        )}
        {isEditable && (
          <div
            className="absolute end-2 top-2 hidden h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white shadow-[0.25rem_0.25rem_0.9375rem_0_rgba(65,92,171,0.15)] group-hover:flex"
            onClick={handleModalOpen}
          >
            <IconComponent className="cursor-pointer" name="editProfile" width="1.25rem" height="1.25rem" />
          </div>
        )}
      </div>

      <ModalStateDialogComponent
        allowModalPadding={false}
        isVisible={openLocationModal}
        width="70%"
        onCancel={handleModalClose}
        headerTitle={
          <div className="flex w-full justify-between">
            <span className="text-[1.75rem] font-bold">{t("Set Location")}</span>
          </div>
        }
        modelContent={
          <div>
            <div className="flex w-full gap-10">
              <div className="flex flex-1 flex-col gap-[1.875rem]">
                <div className="flex">
                  <span style={{ width: "100%" }}>{t("Street Address")}</span>
                  <TextFieldComponent
                    placeholder={t("Street Address")}
                    name="address1"
                    value={data.address1}
                    onChange={handleLocationDetailsChange}
                  />
                </div>
                <div className="flex">
                  <span style={{ width: "100%" }}>{t("Street Address Line {{n, number}}", { n: 2 })}</span>
                  <TextFieldComponent
                    placeholder={t("Street Address Line {{n, number}}", { n: 2 })}
                    name="address2"
                    value={data.address2}
                    onChange={handleLocationDetailsChange}
                  />
                </div>
                <div className="flex">
                  <div className="w-1/2" />
                  <div className="flex w-1/2 flex-col gap-5">
                    <TextFieldComponent
                      placeholder={t("address")}
                      name="address3"
                      value={data.address3}
                      onChange={handleLocationDetailsChange}
                    />
                    {!textBoxVisibleState && (
                      <span className="cursor-pointer text-sm font-bold text-[#6bbbe3]" onClick={textBoxVisible}>
                        {t("+ Add Address Line (Optional)")}
                      </span>
                    )}
                    {textBoxVisibleState && (
                      <TextFieldComponent
                        placeholder={t("address")}
                        name="address4"
                        onChange={handleLocationDetailsChange}
                      />
                    )}
                  </div>
                </div>
                <div className="flex">
                  <span style={{ width: "100%" }}>{t("City/Town/Village")}</span>
                  <TextFieldComponent
                    placeholder={t("City/Town")}
                    name="city"
                    value={data.city}
                    onChange={handleLocationDetailsChange}
                  />
                </div>
                <div className="flex">
                  <span style={{ width: "100%" }}>{t("Pincode")}</span>
                  <TextFieldComponent
                    placeholder={t("Pincode")}
                    type="number"
                    name="pincode"
                    value={data.pincode}
                    onChange={handleLocationDetailsChange}
                  />
                </div>
                <div className="flex">
                  <span style={{ width: "100%" }}>{t("State/Region/Province")}</span>
                  <TextFieldComponent
                    placeholder={t("State/Region/Province")}
                    name="state"
                    value={data.state}
                    onChange={handleLocationDetailsChange}
                  />
                </div>
                <div className="flex">
                  <span style={{ width: "100%" }}>{t("Country")}</span>
                  <TextFieldComponent
                    placeholder={t("Country")}
                    name="country"
                    value={data.country}
                    onChange={handleLocationDetailsChange}
                  />
                </div>
              </div>
              <div className="relative flex-[0.5] rounded-lg bg-[linear-gradient(145deg,#fff_0%,rgba(255,255,255,0.5)_100%)] p-2.5 shadow-[0.25rem_0.25rem_0.9375rem_0_rgba(65,92,171,0.15)]">
                <GoogleMap
                  showLiveLocation={false}
                  autoFetchLiveLocation={!data.coordinates}
                  getLocationCallBack={getLocationCallBack}
                  // @ts-expect-error -- same optional-`lat`/`lng`-into-`LatLng` gap as line 177, plus the value may be `null`.
                  additionalMarker={data.coordinates}
                  zoomLevel={data.mapZoomLevel}
                  fetchAddress
                />
              </div>
            </div>
          </div>
        }
        footerContent={
          <div className="flex gap-5">
            <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleModalClose}>
              {t("Cancel")}
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={onSuccess}>
              {t("Add")}
            </ButtonComponent>
          </div>
        }
      />
    </>
  );
}
export default EditLocationModal;
